/** Service registry list → builder entry (doc 03 §4). */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  Localized,
  ServiceMaturity,
} from '@servicesai/api-client';
import {
  DataTable,
  FilterBar,
  GlassButton,
  GlassInput,
  StatusPill,
  useI18n,
  type DataTableColumn,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile } from '../components/elevated/kit.js';
import { ServiceIcon } from '../components/ServiceIcon.js';

interface ServiceRow {
  slug: string;
  name: Localized;
  status: string;
  has_draft: boolean;
  live_version: number | null;
  category: string;
  ai_enabled: boolean;
  featured_rank: number | null;
  owner_unit: string | null;
  updated_at: string;
  last_published_at: string | null;
  sync_target: string;
  channels: { webchat: boolean; whatsapp: boolean; voice: boolean };
  fields_count: number;
  steps_count: number;
  maturity: ServiceMaturity | null;
}

export function ServicesPage() {
  const { l } = useI18n();
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceRow[] | null>(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [syncTarget, setSyncTarget] = useState('');
  const [channel, setChannel] = useState('');
  const [aiMaturity, setAiMaturity] = useState('');
  const [mlsMaturity, setMlsMaturity] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [lifecycle, setLifecycle] = useState<'pause' | 'resume' | 'retire' | null>(null);
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(() => {
    admin()
      .services()
      .then((res) => setServices(res.services as unknown as ServiceRow[]))
      .catch(() => setError(true));
  }, []);

  useEffect(load, [load]);

  const categories = [...new Set((services ?? []).map((service) => service.category))].sort();
  const syncTargets = [...new Set((services ?? []).map((service) => service.sync_target))].sort();
  // Only the channels some service is actually offered on, so the filter can
  // never advertise a value that returns nothing.
  const channelsOffered = [
    ...new Set(
      (services ?? []).flatMap((service) =>
        Object.entries(service.channels)
          .filter(([, enabled]) => enabled)
          .map(([name]) => name),
      ),
    ),
  ].sort();
  const filtered = (services ?? []).filter((service) => {
    const needle = search.trim().toLocaleLowerCase();
    return (
      (!needle || `${service.slug} ${l(service.name)} ${service.category} ${service.owner_unit ?? ''}`.toLocaleLowerCase().includes(needle)) &&
      (!status || service.status === status) &&
      (!category || service.category === category) &&
      (!syncTarget || service.sync_target === syncTarget) &&
      (!channel || service.channels[channel as keyof ServiceRow['channels']]) &&
      (!aiMaturity || service.maturity?.ai === aiMaturity) &&
      (!mlsMaturity || service.maturity?.mls === mlsMaturity)
    );
  });

  const columns: DataTableColumn<ServiceRow>[] = [
    {
      id: 'service',
      header: l(S.service),
      sortable: true,
      value: (service) => l(service.name),
      render: (service) => (
        <div className="flex min-w-0 items-center gap-3">
          <ServiceIcon slug={service.slug} category={service.category} />
          <div className="min-w-0">
            <div className="max-w-72 truncate text-sm font-bold">{l(service.name)}</div>
            <div className="mt-0.5 truncate font-mono text-[10px] text-ink-faint" dir="ltr">{service.slug}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'category',
      header: l({ en: 'Category', ar: 'الفئة' }),
      sortable: true,
      value: (service) => service.category,
      render: (service) => <span className="text-xs font-semibold">{service.category}</span>,
    },
    {
      id: 'status',
      header: l(S.status),
      sortable: true,
      value: (service) => service.status,
      render: (service) => (
        <div className="flex flex-wrap items-center gap-1">
          <StatusPill state={service.status} label={service.status} />
          {service.has_draft && <span className="aegov-badge">{l(S.draft)}</span>}
        </div>
      ),
    },
    {
      id: 'definition',
      header: l({ en: 'Definition', ar: 'التعريف' }),
      sortable: true,
      value: (service) => service.fields_count,
      render: (service) => (
        <div className="text-xs">
          <div><strong>{service.steps_count}</strong> {l({ en: 'steps', ar: 'خطوات' })}</div>
          <div className="text-ink-faint"><strong>{service.fields_count}</strong> {l({ en: 'fields', ar: 'حقول' })}</div>
        </div>
      ),
    },
    {
      id: 'sync',
      header: l({ en: 'Integration', ar: 'التكامل' }),
      sortable: true,
      value: (service) => service.sync_target,
      render: (service) => (
        <div>
          <span className="font-mono text-xs font-bold">{service.sync_target}</span>
          <div className="mt-1 flex gap-1">
            {Object.entries(service.channels).filter(([, enabled]) => enabled).map(([name]) => <span key={name} className="rounded bg-aeblack-50 px-1.5 py-0.5 text-[9px] font-bold uppercase">{name}</span>)}
          </div>
        </div>
      ),
    },
    {
      id: 'version',
      header: l(S.version),
      sortable: true,
      value: (service) => service.live_version,
      render: (service) => <span className="font-mono text-xs font-bold">{service.live_version === null ? '—' : `v${service.live_version}`}</span>,
    },
    {
      id: 'action',
      header: '',
      align: 'end',
      render: () => <span className="text-xs font-bold text-gold-deep">{l({ en: 'Open Studio', ar: 'فتح الاستوديو' })} →</span>,
    },
  ];

  const submitLifecycle = async () => {
    if (!lifecycle || !reason.trim() || selected.length === 0) return;
    try {
      const results = await Promise.all(selected.map((slug) => admin().requestServiceLifecycle(slug, lifecycle, reason.trim())));
      setToast(`${results.length} ${l({ en: 'lifecycle requests sent for approval', ar: 'طلبات تغيير أُرسلت للاعتماد' })}`);
      setSelected([]);
      setLifecycle(null);
      setReason('');
      load();
    } catch (requestError) {
      setToast(`${l(S.actionFailed)}: ${(requestError as Error).message}`);
    }
  };

  return (
    <div>
      <HeroBand
        eyebrow={l({ en: 'Service catalog', ar: 'سجل الخدمات' })}
        title={l(S.servicesTitle)}
        subtitle={l({ en: 'Governed inventory of every ministry service, its citizen journey, data requirements and connected systems.', ar: 'سجل محكوم لجميع خدمات الوزارة ورحلات المتعاملين ومتطلبات البيانات والأنظمة المرتبطة.' })}
        actions={<GlassButton variant="primary" onClick={() => navigate(`/services/${filtered.find((service) => service.has_draft)?.slug ?? filtered[0]?.slug ?? ''}`)} disabled={filtered.length === 0}>{l({ en: 'Open active draft', ar: 'فتح المسودة النشطة' })}</GlassButton>}
        metrics={
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricTile dark label={l({ en: 'Services', ar: 'الخدمات' })} value={services?.length ?? '—'} hint={`${filtered.length} ${l({ en: 'in current view', ar: 'في العرض الحالي' })}`} />
            <MetricTile dark label={l({ en: 'Transactional AI', ar: 'ذكاء المعاملات' })} value={(services ?? []).filter((service) => service.maturity?.execution === 'transactional').length} hint={l({ en: 'Can open governed applications', ar: 'يمكنها فتح طلبات محكومة' })} accent="#3f8e50" />
            <MetricTile dark label={l({ en: 'AI preparation', ar: 'التجهيز بالذكاء' })} value={(services ?? []).filter((service) => service.maturity?.execution === 'informational_handover').length} hint={l({ en: 'Information and human handover only', ar: 'معلومات وتحويل لموظف فقط' })} accent="#cba344" />
            <MetricTile dark label={l({ en: 'MLS live-ready', ar: 'MLS جاهز للتشغيل' })} value={(services ?? []).filter((service) => service.maturity?.mls === 'live_ready').length} hint={l({ en: 'Explicit contract gate only', ar: 'بوابة عقد صريحة فقط' })} accent="#0173ab" />
          </div>
        }
      />

      {toast && <div className="aegov-alert alert-info mb-3" role="status"><div className="alert-content">{toast}</div></div>}

      <FilterBar summary={`${filtered.length} / ${services?.length ?? 0}`}>
        <GlassInput value={search} onChange={(event) => setSearch(event.target.value)} placeholder={l({ en: 'Search service, slug or owner…', ar: 'ابحث عن خدمة أو رمز أو مالك…' })} className="w-full sm:w-72" />
        {[
          { value: status, set: setStatus, label: l(S.status), values: [...new Set((services ?? []).map((service) => service.status))] },
          { value: category, set: setCategory, label: l({ en: 'Category', ar: 'الفئة' }), values: categories },
          { value: syncTarget, set: setSyncTarget, label: l({ en: 'Sync target', ar: 'هدف الربط' }), values: syncTargets },
          // Derived rather than listed, like the filters above it. The hard
          // coded version still offered `voice`, which stopped being a channel
          // and now matches almost nothing.
          { value: channel, set: setChannel, label: l(S.channel), values: channelsOffered },
          { value: aiMaturity, set: setAiMaturity, label: l({ en: 'AI maturity', ar: 'نضج الذكاء' }), values: ['preparation_only', 'form_audited', 'transactional'] },
          { value: mlsMaturity, set: setMlsMaturity, label: l({ en: 'MLS maturity', ar: 'نضج MLS' }), values: ['candidate', 'matched', 'live_ready', 'not_applicable'] },
        ].map((filter) => (
          <div key={filter.label} className="aegov-form-control control-sm">
            <div className="form-control-input">
              <select value={filter.value} onChange={(event) => filter.set(event.target.value)} aria-label={filter.label}>
                <option value="">{filter.label}: {l(S.all)}</option>
                {filter.values.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
          </div>
        ))}
      </FilterBar>

      {selected.length > 0 && (
        <div className="mb-3 rounded-lg border border-aegold-300 bg-aegold-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold">{selected.length} {l({ en: 'selected', ar: 'محدد' })}</span>
            {(['pause', 'resume', 'retire'] as const).map((action) => <GlassButton key={action} size="sm" variant={action === 'retire' ? 'danger' : 'ghost'} onClick={() => setLifecycle(action)}>{action}</GlassButton>)}
          </div>
          {lifecycle && (
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-aegold-200 pt-3">
              <GlassInput value={reason} onChange={(event) => setReason(event.target.value)} placeholder={l({ en: 'Audited reason for this lifecycle change', ar: 'سبب مدقق لهذا التغيير' })} className="min-w-64 flex-1" />
              <GlassButton variant="primary" disabled={!reason.trim()} onClick={() => void submitLifecycle()}>{l({ en: 'Send for approval', ar: 'إرسال للاعتماد' })}</GlassButton>
              <GlassButton variant="ghost" onClick={() => setLifecycle(null)}>{l(S.cancel)}</GlassButton>
            </div>
          )}
        </div>
      )}

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>}
      {!error && (
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(service) => service.slug}
          loading={services === null}
          emptyTitle={l({ en: 'No services match these filters', ar: 'لا توجد خدمات مطابقة' })}
          filename="service-registry.csv"
          selectedKeys={selected}
          onSelectionChange={setSelected}
          onRowClick={(service) => navigate(`/services/${service.slug}`)}
          pageSize={25}
        />
      )}
    </div>
  );
}
